import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input, Spinner } from '@iq/react-components';

import CustomSelect from '../../CustomSelect';
import ListItem from '../../ListItem';
import EditItemModal from '../../EditItemModal';
import { getVariableSchema, variableUiSchema } from './schemas/variableSchema';
import {
  requestSources,
  getSources,
  getSourceTypes,
  getSourcesLoaded,
  createVariables,
  updateVariable,
  deleteVariable,
} from '../../../bundles/sources';
import { getActiveSite } from '../../../bundles/sites';
import { getStateSets, getStateSetsLoaded } from '../../../bundles/statesets';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../bundles/notifications';
import { getVariables } from '../../../services';
import { camelToSentence, useDebounce, useClientSize } from '../../../utils';
import Heading from '../../Heading';
import Pagination from '../../Pagination';
import ExportTimeSeriesModal from './components/ExportTimeSeriesModal';
import { INTERNAL_DATA_SOURCE } from '../../../constants';
import LabelWithTooltip from '../../LabelWithTooltip';
import config from '../../../config';

const getVariableOptionProperties = (optionProperties) => {
  const titledOptionProperties = {};

  for (const [propertyName, propertySchema] of Object.entries(optionProperties)) {
    titledOptionProperties[propertyName] = {
      ...propertySchema,
      title: camelToSentence(propertyName),
    };
  }

  return titledOptionProperties;
};

const getSourceOptionsSchema = (source, sourceType) => ({
  properties: {
    source_id: { enum: [source.id] },
    options: {
      type: 'object',
      title: 'Options',
      description: 'Additional identifiers and settings. Generally these should not be changed.',
      required: sourceType.schemas.variable.properties.options.required,
      properties: getVariableOptionProperties(
        sourceType.schemas.variable.properties.options.properties
      ),
    },
  },
});

const getVisibleColumnWidths = ({ wizardType, hideSourceFilter, isCompactWizardLayout }) => {
  if (wizardType && hideSourceFilter) {
    return isCompactWizardLayout ? ['-', 54, 46] : ['-', 50, 50];
  }
  if (wizardType) {
    return isCompactWizardLayout ? ['-', 42, 32, 26] : ['-', 35, 35, 30];
  }
  if (hideSourceFilter) {
    return ['-', 25, 25, 20, 30];
  }
  return ['-', 20, 20, 15, 15, 30];
};

const prepUpdate = (rawData) => {
  const selectedVariable = JSON.parse(rawData.options.variableSelect);
  return {
    ...rawData,
    name: rawData.name || selectedVariable.name,
    itemDesignation: rawData.itemDesignation || selectedVariable.itemDesignation,
    dataType: rawData.dataType || selectedVariable.dataType,
    unit: rawData.unit || selectedVariable.unit,
    options: {
      ...rawData.options,
      objectId: selectedVariable.objectId,
      variableId: selectedVariable.variableId,
      itemDesignation: selectedVariable.itemDesignation,
    },
  };
};

const VariableView = ({ wizardType }) => {
  const dispatch = useDispatch();
  const { id: siteId, org } = useSelector(getActiveSite);
  const sources = useSelector(getSources);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const sourceTypes = useSelector(getSourceTypes);
  const statesets = useSelector(getStateSets);
  const statesetsLoaded = useSelector(getStateSetsLoaded);

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState(null);
  const [sortBy, setSortBy] = useState({ sortKey: 'name', desc: false });
  const [page, setPage] = useState(1);
  const [variables, setVariables] = useState(null);
  const [totalVariables, setTotalVariables] = useState(0);
  const [variableSchema, setVariableSchema] = useState(null);
  const [createVariableSchema, setCreateVariableSchema] = useState({});
  const [updateVariableUiSchema, setUpdateVariableUiSchema] = useState({});
  const [createVariableData, setCreateVariableData] = useState({});
  const [showCreateVariableForm, setShowCreateVariableForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const hideSourceFilter = config.onPremise || wizardType === 'onprem';

  const [{ height, width }, clientReference] = useClientSize();
  const isCompactWizardLayout = Boolean(wizardType) && width > 0 && width < 960;

  const isHeightReady = height > 0 && headerHeight > 0;
  // list-container - List-header - spacing 2rem(32)
  const queryLimit = Math.floor((height - headerHeight - 32) / 51);

  useLayoutEffect(() => {
    const headerElement = document.querySelector('.list-container .list-item--header');
    if (headerElement) {
      const ListHeaderHeight = headerElement.getBoundingClientRect().height;
      setHeaderHeight(ListHeaderHeight);
    }
  }, [variables]);

  useEffect(() => {
    if (siteId && sourcesLoaded !== siteId) dispatch(requestSources({ siteId }));
  }, [siteId, sourcesLoaded, dispatch]);

  const selectableSources = useMemo(
    () => sources.filter((source) => source.type !== INTERNAL_DATA_SOURCE),
    [sources]
  );

  useEffect(() => {
    if (sourcesLoaded && selectableSources.length > 0) {
      setSourceFilter(selectableSources[0].id);
    }
  }, [sourcesLoaded, selectableSources]);

  const baseQuery = useMemo(
    () => ({
      limit: queryLimit,
      page,
      order: sortBy.desc ? 'DESC' : 'ASC',
      orderBy: sortBy.sortKey,
      extended: true,
    }),
    [queryLimit, page, sortBy]
  );

  const onRequestVariables = useDebounce(() => {
    const query = { ...baseQuery };
    const vizFilters = selectedFilters.filter((filterValue) =>
      filterValue.includes('visualizations')
    );
    const vizFilter = vizFilters.length === 1 ? vizFilters[0].split('-')[1] : 'all';

    const statesetFilters = selectedFilters.filter((filterValue) =>
      filterValue.includes('stateset')
    );
    const statesetFilter = statesetFilters.length === 1 ? statesetFilters[0].split('-')[1] : 'all';

    query.visualizations = vizFilter;
    query.stateset = statesetFilter;

    if (searchFilter) {
      query.search = searchFilter;
    }

    const fetchVariables = async () => {
      try {
        const { values, total } = await getVariables(sourceFilter, query);
        setVariables(values);
        setTotalVariables(total);
      } catch (error) {
        console.error('Unable to fetch signals:', error);
        dispatch(checkIsOnline());
        dispatch(displayNotification(getNotification('getVariables', 'error')('variable')));
      }
    };

    fetchVariables();
  }, 500);

  useEffect(() => {
    if (sourceFilter && isHeightReady) {
      onRequestVariables();
    } else {
      setVariables([]);
    }
  }, [
    sourceFilter,
    baseQuery,
    queryLimit,
    searchFilter,
    selectedFilters,
    needsRefresh,
    height,
    isHeightReady,
    onRequestVariables,
  ]);

  const sourceFilterOptions = useMemo(() => {
    if (sourcesLoaded && selectableSources.length > 0) {
      return selectableSources.map((source) => ({
        value: source.id,
        label: source.name,
      }));
    }
    return [];
  }, [sourcesLoaded, selectableSources]);

  const filterOptions = [
    { value: 'visualizations-with', label: 'With visualizations' },
    { value: 'visualizations-without', label: 'Without visualizations' },
    { value: 'stateset-with', label: 'With state set' },
    { value: 'stateset-without', label: 'Without state set' },
  ];

  const variableSourceOptions = useMemo(
    () =>
      sources.map((source) => {
        const sourceType = sourceTypes.find((st) => st.name === source.type);
        if (
          Object.keys(sourceType?.schemas?.variable?.properties?.options?.properties || {}).length >
          0
        ) {
          return getSourceOptionsSchema(source, sourceType);
        }
        return {
          properties: {
            source_id: { enum: [source.id] },
          },
        };
      }),
    [sources, sourceTypes]
  );

  // don't include statesets if in wizard
  const updatedVariableSchema = useMemo(() => {
    const states = wizardType ? null : (statesetsLoaded && statesets) || null;
    return getVariableSchema(states);
  }, [statesets, statesetsLoaded, wizardType]);

  useEffect(() => {
    if (sources.length > 0) {
      const filteredSource = sources.find((source) => source.id === sourceFilter);
      setVariableSchema({
        ...updatedVariableSchema,
        required: ['name', 'source_id', 'dataType'],
        properties: {
          ...updatedVariableSchema.properties,
          id: { type: 'string' },
          source_id: {
            ...updatedVariableSchema.properties.source_id,
            type: 'string',
            default: filteredSource?.id || sources[0].id,
            oneOf: sources.map((source) => ({
              enum: [source.id],
              title: source.name,
            })),
          },
        },
        dependencies: {
          source_id: {
            oneOf: variableSourceOptions,
          },
        },
      });
    }
  }, [updatedVariableSchema, sources, variableSourceOptions, sourceFilter]);

  useEffect(() => {
    const filteredSource = sources.find((source) => source.id === sourceFilter);
    if (filteredSource) {
      setUpdateVariableUiSchema({
        ...variableUiSchema,
        ...(filteredSource.type === 'dummy' ? {} : { dataType: { 'ui:disabled': true } }),
        source_id: { 'ui:widget': 'hidden' },
      });
    }
  }, [sourceFilter, sources]);

  useEffect(() => {
    const filteredSources = sources.filter((source) => source.type !== INTERNAL_DATA_SOURCE); // Ignore internal source
    if (filteredSources.length > 0) {
      setCreateVariableSchema({
        ...updatedVariableSchema,
        required: ['name', 'source_id', 'dataType'],
        properties: {
          ...updatedVariableSchema.properties,
          source_id: {
            ...updatedVariableSchema.properties.source_id,
            type: 'string',
            default: filteredSources[0].id,
            oneOf: filteredSources.map((source) => ({
              enum: [source.id],
              title: source.name,
            })),
          },
        },
        dependencies: {
          source_id: {
            oneOf: variableSourceOptions,
          },
        },
      });
    }
  }, [updatedVariableSchema, sources, variableSourceOptions]);

  const onUpdateVariable = useCallback(
    (sourceId, updatedVariable) => {
      const data = { ...updatedVariable };
      if (!data.dataType) {
        data.dataType = null;
      }
      if (!data.unit) {
        data.unit = null;
      }
      dispatch(
        updateVariable(sourceId, data.id, data, () => setNeedsRefresh((previous) => previous + 1))
      );
    },
    [dispatch]
  );

  const onCreateVariable = ({ formData: { source_id: sourceId, ...createData } }) => {
    dispatch(
      createVariables(sourceId, createData, () => setNeedsRefresh((previous) => previous + 1))
    );
    setShowCreateVariableForm(false);
    setCreateVariableData({});
  };

  const onChangeCreateVariable = ({ formData: newVariable }) => {
    if (
      newVariable.options?.variableSelect &&
      newVariable.options.variableSelect !== createVariableData.options.variableSelect
    ) {
      setCreateVariableData(() => prepUpdate(newVariable));
    } else {
      setCreateVariableData(() => newVariable);
    }
  };

  const handleClose = useCallback(
    () => setShowCreateVariableForm(false),
    [setShowCreateVariableForm]
  );
  const handleCloseExportModal = useCallback(
    () => setShowDownloadForm(false),
    [setShowDownloadForm]
  );
  const handleExportCompleted = useCallback(() => {
    handleCloseExportModal();
    setSelectedIds([]);
  }, [handleCloseExportModal]);

  const onDeleteVariable = useCallback(
    (sourceId, variableId) => {
      dispatch(
        deleteVariable(sourceId, variableId, false, () =>
          setNeedsRefresh((previous) => previous + 1)
        )
      );
    },
    [dispatch]
  );

  const onToggleSelection = useCallback((id) => {
    setSelectedIds((previous) => {
      const isSelected = previous.includes(id);
      return isSelected ? previous.filter((selectedId) => selectedId !== id) : [...previous, id];
    });
  }, []);

  const toggleSelectAllVariables = useCallback(
    (event) => {
      event.stopPropagation();
      setSelectedIds((previous) =>
        previous.length === variables.length ? [] : variables.map((v) => v.id)
      );
    },
    [variables]
  );

  const handleSort = (sortKey) => {
    const desc = sortBy.sortKey !== sortKey || !sortBy.desc;
    setSortBy(() => ({ sortKey, desc }));
  };

  let headers = [
    {}, // For select column
    { sortKey: 'name', label: 'Name' },
    { sortKey: 'itemDesignation', label: 'Reference designation' },
    { label: 'Data source' },
    { sortKey: 'stateset', label: 'Linked state set' },
    { sortKey: undefined, label: 'Linked components' },
  ];

  if (wizardType) {
    headers = headers.toSpliced(4, 2); // remove statesets, linked components column
  }
  if (hideSourceFilter) {
    headers = headers.toSpliced(3, 1); // remove data source column for on-prem
  }

  const onSearchFilter = useCallback((event) => {
    setPage(1);
    setSearchFilter((event.target.value || '').trim());
  }, []);

  const handleSourceFilterChange = useCallback((sourceId) => {
    setPage(1);
    setSourceFilter(sourceId);
  }, []);

  const handleSelectedFiltersChange = useCallback((filters) => {
    setPage(1);
    setSelectedFilters(filters);
  }, []);

  const headerColumns = headers.map((h, index) => {
    const key = `${h.sortKey || h.label}-${index}`;
    if (!h.sortKey) {
      return <div key={key}>{h.label}</div>;
    }
    const icon = sortBy.sortKey === h.sortKey && sortBy.desc ? 'he-down' : 'he-up';
    return (
      <Button
        key={key}
        onClick={() => handleSort(h.sortKey)}
        icon={
          <Icon
            icon={icon}
            size="xs"
          />
        }
        iconPosition="right"
        activity="secondary"
      >
        {h.label}
      </Button>
    );
  });

  const getListItemColumns = useCallback(
    (variable) => {
      const isSelected = selectedIds.includes(variable.id);
      const cols = [
        <div
          key={`${variable.id}-col-1`}
          className="table-cell select-column"
        >
          <Button design="text">
            <Icon
              className={`checkbox ${isSelected ? 'checked' : ''}`}
              icon={`${isSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
              size="s"
              onClick={(event) => {
                event.stopPropagation();
                onToggleSelection(variable.id);
              }}
            />
          </Button>
        </div>,
        <>
          {variable.visualizations?.length ? (
            <Icon
              icon={'he-line-chart-alt'}
              size="s"
            />
          ) : null}
          <LabelWithTooltip name={variable.name} />
        </>,
        <LabelWithTooltip
          key={`${variable.id}-col-3`}
          name={variable.itemDesignation}
        />,
        <LabelWithTooltip
          key={`${variable.id}-col-4`}
          name={variable.source?.name}
        />,
        <LabelWithTooltip
          key={`${variable.id}-col-5`}
          name={`${variable.stateset?.name || ''}`}
        />,
        <div
          key={`${variable.id}-col-6`}
          className="blocked-item-container"
        >
          {variable.components.map((c, index) => (
            <div
              key={`comp-${index}`}
              className="blocked-item"
            >
              <LabelWithTooltip name={`${c.name} (${c.itemDesignation})`} />
            </div>
          ))}
        </div>,
      ];

      // don't include statesets if in wizard
      if (wizardType) {
        cols.splice(4, 2);
      }
      if (hideSourceFilter) {
        cols.splice(3, 1); // remove data source column for on-prem
      }
      return cols;
    },
    [selectedIds, onToggleSelection, wizardType, hideSourceFilter]
  );

  const allSelected = variables?.length && variables?.length === selectedIds.length;

  const visibleColumnWidths = useMemo(
    () => getVisibleColumnWidths({ wizardType, hideSourceFilter, isCompactWizardLayout }),
    [wizardType, hideSourceFilter, isCompactWizardLayout]
  );

  const columnWidths = useMemo(
    () => ({
      type: 'percent',
      widths: visibleColumnWidths,
    }),
    [visibleColumnWidths]
  );

  const manageVariablesBodyClassName = [
    'manage-variables-body',
    wizardType ? 'manage-variables-body--wizard' : '',
    isCompactWizardLayout ? 'manage-variables-body--wizard-compact' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const listContainerClassName = [
    'list-container',
    wizardType ? 'list-container--wizard' : '',
    isCompactWizardLayout ? 'list-container--wizard-compact' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const signalList = useMemo(
    () => (
      <div className="custom-thin-scrollbar">
        {variables?.map((v) => (
          <ListItem
            key={v.id}
            entity={`Signal (${v.name})`}
            item={v}
            columns={getListItemColumns(v)}
            columnWidths={columnWidths}
            onSubmit={(update) => onUpdateVariable(v.source_id, update)}
            onDelete={() => onDeleteVariable(v.source_id, v.id)}
            deletePermission="variables/Delete"
            permissionScope={{ org, site: siteId }}
            confirmationDialogTitle={'Delete signal'}
            confimationDialogBody={
              <p style={{ paddingTop: '1.5rem' }}>Are you sure you want to remove this signal?</p>
            }
            schema={variableSchema}
            uiSchema={updateVariableUiSchema}
            withSelect
          />
        ))}
      </div>
    ),
    [
      variables,
      getListItemColumns,
      columnWidths,
      variableSchema,
      updateVariableUiSchema,
      onUpdateVariable,
      onDeleteVariable,
    ]
  );

  const wizClass = wizardType ? `--${wizardType}` : '';

  return (
    <>
      <div className={`signals-heading-row${wizardType ? ' signals-heading-row--wizard' : ''}`}>
        <Heading
          contentLeft={
            <div
              className={`signals-header${wizardType ? ' signals-header--wizard' : ''}${isCompactWizardLayout ? ' signals-header--compact' : ''}`}
            >
              <div className="title">Signals</div>
              <div
                className={`filter-container${wizardType && hideSourceFilter ? '--full-width' : ''}`}
              >
                <Input
                  type="text"
                  onChange={onSearchFilter}
                  value={searchFilter}
                  placeholder="Search signal name"
                />

                {hideSourceFilter ? null : (
                  <div className="source-select">
                    <CustomSelect
                      isMulti={false}
                      onChange={handleSourceFilterChange}
                      creatable={false}
                      value={sourceFilter}
                      rawOptions={sourceFilterOptions}
                      closeMenuOnSelect={true}
                      placeholder="No sources available"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minWidth: '100%',
                          height: '36px',
                          minHeight: '36px',
                        }),
                      }}
                    />
                  </div>
                )}

                <div className="filter-select">
                  <CustomSelect
                    isMulti={true}
                    popout
                    onChange={handleSelectedFiltersChange}
                    label="Signal filters"
                    creatable={false}
                    value={selectedFilters}
                    rawOptions={filterOptions}
                    closeMenuOnSelect={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minWidth: '100%',
                        minHeight: '36px',
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          }
          contentRight={
            !config.onPremise && (
              <Button
                onClick={() => setShowCreateVariableForm(true)}
                className={`button-create${wizardType ? wizClass : ''}`}
              >
                Add Signal
              </Button>
            )
          }
        />
      </div>
      <div className={manageVariablesBodyClassName}>
        <p className="signals">{`Manage signals (${totalVariables})`} </p>

        <div className="selection-toggler">
          <label className="select-all-label">
            <Icon
              className={`select-all ${allSelected ? 'checked' : ''}`}
              icon={`${allSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
              size="s"
              onClick={toggleSelectAllVariables}
            />
            Select all
          </label>
          {selectedIds.length > 0 && (
            <Button
              onClick={() => setShowDownloadForm(true)}
              activity="secondary"
              icon={<Icon icon="he-download" />}
              tooltip="Download selected"
              slim={true}
            />
          )}
        </div>
        <div
          className={listContainerClassName}
          ref={clientReference}
        >
          <ListItem
            isHeader
            columns={headerColumns}
            columnWidths={columnWidths}
            withSelect
          />
          {variables ? (
            signalList
          ) : (
            <div className="signal-loader">
              <Spinner
                size="s"
                className="spinner"
              />
            </div>
          )}
        </div>
        <Pagination
          pages={Math.ceil(totalVariables / queryLimit)}
          page={page}
          setPage={setPage}
        />
      </div>
      {showCreateVariableForm && (
        <EditItemModal
          entity="Signal"
          formData={createVariableData}
          schema={createVariableSchema}
          uiSchema={variableUiSchema}
          onChange={onChangeCreateVariable}
          onSubmit={onCreateVariable}
          onCloseModal={handleClose}
        />
      )}
      {showDownloadForm && selectedIds.length > 0 && (
        <ExportTimeSeriesModal
          selectedVariableIds={selectedIds}
          onCloseModal={handleCloseExportModal}
          onExportCompleted={handleExportCompleted}
        />
      )}
    </>
  );
};

export default VariableView;
